'use client';
import React, { useState } from 'react';
import { Mic2, Music, Dices, Brain, PartyPopper, Building2, GraduationCap, Heart } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const services = [
  {
    id: 'service-weddings',
    icon: Heart,
    title: 'Weddings',
    subtitle: 'Reception — Ceremony Available as Add-On',
    description: 'Reception DJ sets, grand entrances, first dances with phonetic pronunciation guides. Ceremony coverage, wedding party microphones, and ordained officiant services by Christopher Harris are all available as DJ add-ons.',
    rate: 'Starting at $200/hr',
    minimum: '4-Hour minimum',
    color: 'var(--neon-pink)',
    features: ['Reception DJ', 'Grand Entrance Coordination', 'Timeline Builder', 'Vendor Coordination', 'Add-On: Ceremony, Mics & Officiant'],
    badge: 'Most Popular',
  },
  {
    id: 'service-karaoke',
    icon: Mic2,
    title: 'Karaoke Nights',
    subtitle: 'Full Karaoke Setup',
    description: 'Professional karaoke system with thousands of songs, hosted by DJ ExXtra. Perfect for bars, private parties, corporate events, and fundraisers.',
    rate: '$75/hr',
    color: 'var(--neon-cyan)',
    features: ['Full Song Library', 'Pro Sound System', 'Crowd Hosting', 'Custom Song Requests', 'Spotlight Effects'],
    badge: null,
  },
  {
    id: 'service-bingo',
    icon: Dices,
    title: 'Bingo Nights',
    subtitle: 'Hosted & DJ-Mixed',
    description: 'High-energy bingo nights with music-mixed calling, custom prize structures, and crowd engagement. Great for bars and community events.',
    rate: '$75/hr',
    color: 'var(--neon-gold)',
    features: ['Custom Bingo Cards', 'Music-Mixed Calling', 'Prize Management', 'Crowd Energy', 'Themed Nights'],
    badge: null,
  },
  {
    id: 'service-trivia',
    icon: Brain,
    title: 'Trivia Nights',
    subtitle: 'Interactive Trivia',
    description: 'Custom trivia categories, digital scorekeeping, and DJ-mixed music rounds. Keeps your crowd engaged and coming back every week.',
    rate: '$75/hr',
    color: 'var(--neon-purple)',
    features: ['Custom Categories', 'Digital Scoring', 'Music Rounds', 'Prize Coordination', 'Weekly Themes'],
    badge: null,
  },
  {
    id: 'service-parties',
    icon: PartyPopper,
    title: 'Private Parties',
    subtitle: 'All Occasions',
    description: 'Birthday parties, anniversaries, retirement celebrations, and more. Custom playlists, Spotify integration, and guest song request links.',
    rate: '$75/hr',
    color: 'var(--neon-pink)',
    features: ['Custom Playlists', 'Guest Song Requests', 'Spotify Integration', 'Photo Booth Add-On', 'Lighting Packages'],
    badge: null,
  },
  {
    id: 'service-corporate',
    icon: Building2,
    title: 'Corporate Events',
    subtitle: 'Professional & Polished',
    description: 'Company parties, award ceremonies, product launches, and team building events. Professional sound and presentation-ready AV setup.',
    rate: '$75/hr',
    color: 'var(--neon-cyan)',
    features: ['AV Presentation', 'Background Music', 'Award Ceremonies', 'Networking Events', 'Branded Playlists'],
    badge: null,
  },
  {
    id: 'service-graduation',
    icon: GraduationCap,
    title: 'Graduations',
    subtitle: 'Ceremonies & Parties',
    description: 'Graduation ceremonies with processional music and parties to celebrate the big milestone. School-appropriate and parent-approved.',
    rate: '$75/hr',
    color: 'var(--neon-gold)',
    features: ['Processional Music', 'Ceremony Sound', 'After-Party DJ', 'Custom Mixes', 'Photo Moments'],
    badge: null,
  },
  {
    id: 'service-bars',
    icon: Music,
    title: 'Bar & Venue Events',
    subtitle: 'Regular & Special Events',
    description: 'Weekly residencies, special themed nights, and holiday events. Build your bar\'s reputation with consistent high-energy entertainment.',
    rate: '$75/hr',
    color: 'var(--neon-purple)',
    features: ['Weekly Residencies', 'Themed Nights', 'Holiday Events', 'Crowd Reading', 'Genre Mixing'],
    badge: 'Available Weekly',
  },
];

export default function ServicesSection() {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  return (
    <section id="services" className="py-24 relative">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        {/* Section header */}
        <div className="text-center mb-16">
          <p className="text-xs font-mono tracking-[0.4em] text-muted-foreground uppercase mb-3">What We Do</p>
          <h2 className="text-4xl md:text-5xl font-extrabold mb-4">
            <span className="gold-gradient-text">Event Services</span>
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto text-base">
            From intimate ceremonies to packed bar nights — DJ ExXtra delivers professional entertainment with maximum effort, every time.
          </p>
        </div>

        {/* Service cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {services?.map((service) => {
            const Icon = service?.icon;
            const isHovered = hoveredId === service?.id;
            return (
              <div
                key={service?.id}
                className="relative rounded-2xl p-6 card-glass cursor-pointer transition-all duration-300 kanban-card-hover group"
                style={{
                  borderColor: isHovered ? service?.color : undefined,
                  boxShadow: isHovered ? `0 0 30px ${service?.color}30, 0 4px 20px rgba(0,0,0,0.4)` : undefined,
                }}
                onMouseEnter={() => setHoveredId(service?.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                {/* Badge */}
                {service?.badge && (
                  <div className="absolute top-4 right-4 px-2 py-0.5 rounded-full text-xs font-bold gold-gradient text-black">
                    {service?.badge}
                  </div>
                )}
                {/* Icon */}
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-all duration-300"
                  style={{
                    background: `${service?.color}15`,
                    border: `1px solid ${service?.color}40`,
                    boxShadow: isHovered ? `0 0 20px ${service?.color}30` : 'none',
                  }}
                >
                  <Icon size={22} style={{ color: service?.color }} />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-0.5">{service?.title}</h3>
                <p className="text-xs font-mono text-muted-foreground mb-3">{service?.subtitle}</p>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">{service?.description}</p>
                {/* Features */}
                <ul className="flex flex-wrap gap-1.5 mb-4">
                  {service?.features?.map((feat) => (
                    <li
                      key={`feat-${service?.id}-${feat}`}
                      className="text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{
                        background: `${service?.color}10`,
                        border: `1px solid ${service?.color}30`,
                        color: service?.color,
                      }}
                    >
                      {feat}
                    </li>
                  ))}
                </ul>
                {/* Rate */}
                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <span className="text-xs text-muted-foreground font-mono">Starting at</span>
                  <div className="flex flex-col items-end">
                    <span className="text-base font-bold gold-gradient-text font-mono">{service?.rate}</span>
                    {service?.minimum && (
                      <span className="text-xs text-muted-foreground font-mono">{service?.minimum}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Add-on services */}
        <div className="mt-16 rounded-2xl neon-border-gold p-8 card-glass">
          <h3 className="text-2xl font-bold text-foreground mb-2 text-center">Event Add-On Services</h3>
          <p className="text-sm text-muted-foreground text-center mb-8">Enhance your event with our curated vendor network. All vendors are vetted and dispatched automatically upon booking.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {[
              { label: 'Officiant', emoji: '⛪' },
              { label: 'Bartenders', emoji: '🍸' },
              { label: 'Limo / Transport', emoji: '🚗' },
              { label: 'Security', emoji: '🛡️' },
              { label: 'Photo Booth', emoji: '📸' },
              { label: 'Live Musicians', emoji: '🎸' },
              { label: 'Dancing on Clouds', emoji: '☁️' },
              { label: 'Catering', emoji: '🍽️' },
              { label: 'Florist', emoji: '💐' },
              { label: 'Baker', emoji: '🎂' },
              { label: 'Decorators', emoji: '✨' },
              { label: 'Photographer', emoji: '📷' },
              { label: 'Videographer', emoji: '🎥' },
              { label: 'Hair & Makeup', emoji: '💄' },
            ]?.map((addon) => (
              <div
                key={`addon-${addon?.label}`}
                className="flex flex-col items-center gap-2 p-3 rounded-xl border border-border hover:border-primary/40 hover:bg-primary/5 transition-all duration-200 cursor-pointer"
              >
                <span className="text-2xl">{addon?.emoji}</span>
                <span className="text-xs font-medium text-muted-foreground text-center leading-tight">{addon?.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}